import { useCallback, useEffect, useMemo } from "react";
import type { TablePaginationConfig } from "antd/es/table";

import { HandbookFilter } from "features/handbook/model/handbookFilter";
import { useLazyGetHandbookQuery } from "features/handbook/api/handbookApi";
import { ALL_SERVICES_CODE, SERVICE_CATEGORY_CODE } from "./useHandbookRoute";

type Args = {
  dictCode?: string;
  serviceCode?: string;
  pagination: TablePaginationConfig;
  allDictPagination: TablePaginationConfig;
  debouncedSearch: string;
  /** Меняется при переключении прод/тест — заставляет перезапросить данные. */
  apiVersion: number;
  defaultPageSize: number;
  defaultAllPageSize: number;
};

/**
 * Страница показывает два списка: записи текущего сервиса и все доступные записи
 * справочника. Оба тянутся одним и тем же эндпоинтом, отличаются только фильтром.
 */
export function useHandbookLists({
  dictCode,
  serviceCode,
  pagination,
  allDictPagination,
  debouncedSearch,
  apiVersion,
  defaultPageSize,
  defaultAllPageSize,
}: Args) {
  const [fetchList, { data, isFetching }] = useLazyGetHandbookQuery();
  const [fetchListAll, { data: allData, isFetching: isFetchingAll }] =
    useLazyGetHandbookQuery();

  const rows = data?.res?.content ?? [];
  const total = data?.res?.totalElements ?? 0;
  const page = pagination.current ?? data?.res?.page ?? 1;
  const pageSize = pagination.pageSize ?? data?.res?.size ?? defaultPageSize;

  const allDictRows = allData?.res?.content ?? [];
  const allDictTotal = allData?.res?.totalElements ?? 0;
  const allDictPage = allDictPagination.current ?? allData?.res?.page ?? 1;
  const allDictPageSize =
    allDictPagination.pageSize ?? allData?.res?.size ?? defaultAllPageSize;

  const normalizedServiceCode =
    serviceCode === ALL_SERVICES_CODE ? null : serviceCode;

  const currentFilter = useMemo(() => {
    const f = new HandbookFilter();
    f.page = page ?? 1;
    f.size = pageSize;
    f.serviceCode = normalizedServiceCode;
    f.deleted = false;
    f.name = debouncedSearch || "";
    return f;
  }, [page, pageSize, normalizedServiceCode, debouncedSearch]);

  const currentAllFilter = useMemo(() => {
    const f = new HandbookFilter();
    f.page = allDictPage ?? 1;
    f.size = allDictPageSize;
    f.serviceCode = null;
    f.deleted = false;
    f.name = debouncedSearch || "";
    return f;
  }, [allDictPage, allDictPageSize, debouncedSearch]);

  useEffect(() => {
    if (!dictCode || dictCode === SERVICE_CATEGORY_CODE) return;
    fetchList({ slug: dictCode, filter: currentFilter });
  }, [dictCode, currentFilter, fetchList, apiVersion]);

  useEffect(() => {
    if (!dictCode || dictCode === SERVICE_CATEGORY_CODE) return;
    fetchListAll({ slug: dictCode, filter: currentAllFilter });
  }, [dictCode, currentAllFilter, fetchListAll, apiVersion]);

  /** Перезапрос только списка текущего сервиса. */
  const reloadCurrent = useCallback(
    () => fetchList({ slug: dictCode!, filter: currentFilter }),
    [fetchList, dictCode, currentFilter],
  );

  /** Перезапрос обоих списков — после привязки/отвязки меняются оба. */
  const reloadBoth = useCallback(
    () =>
      Promise.all([
        fetchList({ slug: dictCode!, filter: currentFilter }),
        fetchListAll({ slug: dictCode!, filter: currentAllFilter }),
      ]),
    [fetchList, fetchListAll, dictCode, currentFilter, currentAllFilter],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    isFetching,
    allDictRows,
    allDictTotal,
    allDictPage,
    allDictPageSize,
    isFetchingAll,
    reloadCurrent,
    reloadBoth,
  };
}
